import React, { useEffect, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';

const ParticlesBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { theme } = useTheme();
  const animationRef = useRef<number>();
  const mouseRef = useRef<{ x: number; y: number } | null>(null);
  const particlesRef = useRef<Array<{
    x: number;
    y: number;
    vx: number;
    vy: number;
    originalVx: number; // Store original velocity
    originalVy: number;
  }>>([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };

    const initParticles = () => {
      particlesRef.current = [];
      const particleCount = 120; // ADJUSTABLE: Number of particles
      
      for (let i = 0; i < particleCount; i++) {
        const vx = (Math.random() - 0.5) * 1.5; // ADJUSTABLE: Movement speed
        const vy = (Math.random() - 0.5) * 1.5;
        particlesRef.current.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: vx,
          vy: vy,
          originalVx: vx, // Store original drift velocity
          originalVy: vy,
        });
      }
    };

    const drawParticles = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      const isDark = theme === 'dark';
      const particleColor = isDark ? '#888' : '#999';
      const linkColor = isDark ? '#666' : '#777';
      
      // Draw links with distance-based opacity
      ctx.strokeStyle = linkColor;
      ctx.lineWidth = 1;
      
      for (let i = 0; i < particlesRef.current.length; i++) {
        for (let j = i + 1; j < particlesRef.current.length; j++) {
          const dx = particlesRef.current[i].x - particlesRef.current[j].x;
          const dy = particlesRef.current[i].y - particlesRef.current[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          const maxDistance = 200; // ADJUSTABLE: Link distance - INCREASED for longer connections
          if (distance < maxDistance) {
            // Calculate opacity based on distance (closer = more opaque)
            const opacity = 0.8 * (1 - distance / maxDistance); // ADJUSTABLE: Max opacity - INCREASED for brighter lines
            ctx.globalAlpha = opacity;
            
            ctx.beginPath();
            ctx.moveTo(particlesRef.current[i].x, particlesRef.current[i].y);
            ctx.lineTo(particlesRef.current[j].x, particlesRef.current[j].y);
            ctx.stroke();
          }
        }
      }
      
      
      // Draw particles
      ctx.fillStyle = particleColor;
      ctx.globalAlpha = 0.6; // ADJUSTABLE: Particle opacity
      
      particlesRef.current.forEach(particle => {
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, 3, 0, Math.PI * 2); // ADJUSTABLE: Particle size
        ctx.fill();
      });
    };

    const updateParticles = () => {
      particlesRef.current.forEach(particle => {
        // Start with original drift velocity
        let newVx = particle.originalVx;
        let newVy = particle.originalVy;
        
        // Add mouse repel effect
        if (mouseRef.current) {
          const dx = particle.x - mouseRef.current.x;
          const dy = particle.y - mouseRef.current.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const repelDistance = 200; // ADJUSTABLE: Repel range
          
          if (distance < repelDistance && distance > 0) {
            // Calculate repel force (stronger when closer)
            const force = (repelDistance - distance) / repelDistance;
            const repelStrength = 5.0; // ADJUSTABLE: Repel strength
            
            // Normalize direction and apply repel force
            const normalizedDx = dx / distance;
            const normalizedDy = dy / distance;
            
            newVx += normalizedDx * force * repelStrength;
            newVy += normalizedDy * force * repelStrength;
          }
        }
        
        // Update particle velocity and position
        particle.vx = newVx;
        particle.vy = newVy;
        particle.x += particle.vx;
        particle.y += particle.vy;
        
        // Bounce off edges
        if (particle.x <= 0 || particle.x >= canvas.width) {
          particle.vx = -particle.vx;
          particle.originalVx = -particle.originalVx; // Also flip original velocity
        }
        if (particle.y <= 0 || particle.y >= canvas.height) {
          particle.vy = -particle.vy;
          particle.originalVy = -particle.originalVy; // Also flip original velocity
        }
      });
    };

    const animate = () => {
      updateParticles();
      drawParticles();
      animationRef.current = requestAnimationFrame(animate);
    };

    resizeCanvas();
    initParticles();
    animate();

    const handleHeroMouseMove = (e: any) => {
      mouseRef.current = {
        x: e.detail.x,
        y: e.detail.y,
      };
    };

    const handleHeroMouseLeave = () => {
      mouseRef.current = null;
    };

    const handleResize = () => {
      resizeCanvas();
      initParticles();
    };

    window.addEventListener('heroMouseMove', handleHeroMouseMove);
    window.addEventListener('heroMouseLeave', handleHeroMouseLeave);
    window.addEventListener('resize', handleResize);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      window.removeEventListener('heroMouseMove', handleHeroMouseMove);
      window.removeEventListener('heroMouseLeave', handleHeroMouseLeave);
      window.removeEventListener('resize', handleResize);
    };
  }, [theme]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 1,
        pointerEvents: 'auto',
      }}
    />
  );
};

export default ParticlesBackground;